import { useState } from "react";
import type { GroundingSource } from "@/services/api";

/**
 * Get clean URL without query parameters for display.
 */
function getCleanUrl(uri: string): string {
  try {
    const url = new URL(uri);
    return url.origin + url.pathname;
  } catch {
    return uri.split('?')[0].split('#')[0];
  }
}

/**
 * Group sources by their cleaned URL.
 * Returns one entry per unique URL with all snippets collected.
 */
function groupSourcesByUrl(sources: GroundingSource[]): GroundingSource[][] {
  const map = new Map<string, GroundingSource[]>();
  for (const source of sources) {
    const key = getCleanUrl(source.uri);
    const existing = map.get(key);
    if (existing) {
      existing.push(source);
    } else {
      map.set(key, [source]);
    }
  }
  return Array.from(map.values());
}

interface CitationBadgeProps {
  /** The citation index (1-based) */
  index: number;
  /** The grouped sources for this URL */
  sources: GroundingSource[];
  /** Callback when the badge is clicked */
  onClick: (sources: GroundingSource[], index: number) => void;
}

/**
 * Inline citation badge that displays as a superscript number.
 * Clicking opens the citation modal with full source details.
 */
export function CitationBadge({ index, sources, onClick }: CitationBadgeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const primarySource = sources[0];

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(sources, index);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative inline-flex items-center justify-center"
      title={primarySource.title || primarySource.uri}
    >
      <span
        className={`
          inline-flex items-center justify-center
          min-w-[1.25rem] h-5 px-1
          text-[10px] font-semibold
          rounded-full
          transition-all duration-200
          cursor-pointer
          ${isHovered
            ? "bg-amber-500 text-white scale-110"
            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
          }
        `}
      >
        {index}
      </span>

      {/* Hover tooltip preview — shows source URL */}
      {isHovered && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 max-w-xs shadow-lg">
            <p className="truncate text-amber-300">{getCleanUrl(primarySource.uri)}</p>
            {sources.length > 1 && (
              <p className="text-slate-400 text-[10px] mt-0.5">{sources.length} excerpts from this page</p>
            )}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
          </div>
        </div>
      )}
    </button>
  );
}

interface CitationBadgesProps {
  /** Array of grounding sources */
  sources?: GroundingSource[];
  /** Callback when a badge is clicked — receives all sources for that URL */
  onCitationClick: (sources: GroundingSource[], index: number) => void;
  /** Optional className for the container */
  className?: string;
}

/**
 * Renders a row of citation badges, one per unique source URL.
 * Multiple sources from the same URL are aggregated into a single badge.
 */
export function CitationBadges({ sources, onCitationClick, className = "" }: CitationBadgesProps) {
  if (!sources || sources.length === 0) return null;

  const grouped = groupSourcesByUrl(sources);

  return (
    <span className={`inline-flex items-center gap-0.5 ml-1 ${className}`}>
      {grouped.map((group, idx) => (
        <CitationBadge
          key={idx}
          index={idx + 1}
          sources={group}
          onClick={onCitationClick}
        />
      ))}
    </span>
  );
}
